import { Link } from "@tanstack/react-router";
import type {
  CellStyleFunc,
  ColDef,
  ICellRendererParams,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { BriefingsStatusFilter } from "@/components/scheduled-briefings/briefings-index/BriefingsIndexContentSection";
import { Skeleton } from "@/components/ui/feedback/skeleton";
import type {
  BriefingTableOrdering,
  BriefingTableTabCounts,
} from "@/data-access/scheduled-briefings/queries/briefing-table-queries";
import { agGridTheme } from "@/lib/ag-grid-theme";
import { cn } from "@/lib/utils";
import BriefingStatusPill, {
  briefingStatusTitleRailColor,
} from "./BriefingStatusPill";
import {
  BriefingsTableOverlayProvider,
  createBriefingsTableUnifiedOverlay,
} from "./BriefingsTableStates";
import type {
  BriefingsTableRow,
  BriefingsTableStatus,
} from "./briefingsTable.types";
import {
  createBriefingsTableDatasource,
  startNewBriefingsTableDatasourceSession,
} from "./briefingsTableDatasource";

const CACHE_BLOCK_SIZE = 50;
const MAX_BLOCKS_IN_CACHE = 10;
const INFINITE_INITIAL_ROW_COUNT = 5;
const TITLE_ACCENT_BORDER_WIDTH_PX = 2;

const DEFAULT_ORDERING: BriefingTableOrdering = {
  updatedAt: "DESC_NULLS_LAST",
};

/** Matches the Last Generated column's `initialSort`, so the first request needs no re-sort. */
const INITIAL_ORDERING: BriefingTableOrdering = {
  lastRunDate: "DESC_NULLS_LAST",
};

const COL_ID_TO_ORDERING_FIELD: Record<string, keyof BriefingTableOrdering> = {
  title: "name",
  lastGenerated: "lastRunDate",
};

export interface BriefingsTableProps {
  searchTerm: string;
  statusTab: BriefingsStatusFilter;
  showStatus: boolean;
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  isInitialLoading: boolean;
  isFiltered: boolean;
  onClearFilters?: () => void;
  onClearError: () => void;
  onLoadingChange: (isLoading: boolean) => void;
  onErrorChange: (message: string | null) => void;
  onPageLoaded: (payload: {
    totalCount: number;
    tabCounts: BriefingTableTabCounts;
  }) => void;
}

const titleCellBaseStyle = {
  display: "flex",
  alignItems: "center",
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "20px",
};

const titleCellStyleWithStatusRail: CellStyleFunc<BriefingsTableRow> = (
  params,
) => {
  const status = params.data?.status;
  const railColor = status ? briefingStatusTitleRailColor(status) : undefined;

  return {
    ...titleCellBaseStyle,
    ...(railColor
      ? {
          borderLeft: `${TITLE_ACCENT_BORDER_WIDTH_PX}px solid ${railColor}`,
        }
      : {}),
  };
};

const briefingsTableSkeletonCell = () => (
  <Skeleton className="h-4 w-full align-middle" />
);

const briefingsTableRecipientCountLabel = (count: number): string => {
  if (count === 0) {
    return "—";
  }
  return count === 1 ? "1 recipient" : `${count} recipients`;
};

/**
 * @public Email avatar stack; not wired as the grid column renderer in this version.
 */
export const BriefingsTableRecipientsCell = ({
  data,
}: ICellRendererParams<BriefingsTableRow>) => {
  if (!data) {
    return briefingsTableSkeletonCell();
  }

  const recipients = data.recipients ?? [];
  if (recipients.length === 0) {
    return null;
  }

  const visibleRecipients = recipients.slice(0, 3);
  const overflowCount = recipients.length - visibleRecipients.length;

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center">
        {visibleRecipients.map((recipient, index) => (
          <span
            key={recipient.id}
            className={cn(
              "flex size-6 items-center justify-center rounded-full border-2 border-white bg-gray-100 text-[10px] font-medium uppercase tracking-wide text-gray-500",
              index > 0 && "-ml-2",
            )}
            style={{ zIndex: visibleRecipients.length - index }}
          >
            {recipient.initials}
          </span>
        ))}
      </div>
      {overflowCount > 0 ? (
        <span className="text-[13px] text-gray-900">+{overflowCount}</span>
      ) : null}
    </div>
  );
};

const BriefingsTableRecipientsCountCell = ({
  data,
}: ICellRendererParams<BriefingsTableRow>) => {
  if (!data) {
    return briefingsTableSkeletonCell();
  }

  return briefingsTableRecipientCountLabel(data.recipients?.length ?? 0);
};

const BriefingsTableTitleCell = ({
  data,
}: ICellRendererParams<BriefingsTableRow>) => {
  if (!data) {
    return briefingsTableSkeletonCell();
  }

  return (
    <Link
      to="/scheduled-briefings/$briefingId"
      params={{ briefingId: data.id }}
      className="text-gray-900 hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-violet-500"
    >
      {data.title}
    </Link>
  );
};

const BriefingsTableStatusCell = ({
  value,
  data,
}: ICellRendererParams<BriefingsTableRow, BriefingsTableStatus>) => {
  if (!data) {
    return briefingsTableSkeletonCell();
  }
  if (!value) {
    return null;
  }

  return <BriefingStatusPill status={value} />;
};

const textCellRenderer = (params: ICellRendererParams<BriefingsTableRow>) => {
  if (!params.data) {
    return briefingsTableSkeletonCell();
  }
  const value = params.value;
  if (value == null || value === "") {
    return "—";
  }
  return String(value);
};

const BriefingsTable = ({
  searchTerm,
  statusTab,
  showStatus,
  isLoading,
  error,
  totalCount,
  isInitialLoading,
  isFiltered,
  onClearFilters,
  onClearError,
  onLoadingChange,
  onErrorChange,
  onPageLoaded,
}: BriefingsTableProps) => {
  const gridRef = useRef<AgGridReact<BriefingsTableRow>>(null);
  const [ordering, setOrdering] =
    useState<BriefingTableOrdering>(INITIAL_ORDERING);

  const datasource = useMemo(() => {
    startNewBriefingsTableDatasourceSession();
    return createBriefingsTableDatasource(
      () => ({ searchMatch: searchTerm, statusTab }),
      () => ordering,
      {
        setIsLoading: onLoadingChange,
        setError: onErrorChange,
        onPageLoaded,
      },
    );
  }, [
    searchTerm,
    statusTab,
    ordering,
    onLoadingChange,
    onErrorChange,
    onPageLoaded,
  ]);

  useEffect(() => {
    if (!gridRef.current?.api) {
      return;
    }
    gridRef.current.api.purgeInfiniteCache();
    gridRef.current.api.refreshInfiniteCache();
  }, [datasource]);

  const handleRetry = useCallback(() => {
    gridRef.current?.api?.refreshInfiniteCache();
  }, []);

  const shouldShowOverlay =
    !isLoading && !error && !isInitialLoading && totalCount === 0;
  const shouldShowErrorOverlay = !isLoading && !!error;

  const UnifiedOverlay = useMemo(
    () =>
      createBriefingsTableUnifiedOverlay(
        showStatus
          ? "No briefings match your search or filter"
          : "No briefings match your search",
        handleRetry,
      ),
    [showStatus, handleRetry],
  );

  useEffect(() => {
    if (!gridRef.current?.api) {
      return;
    }
    if (shouldShowErrorOverlay || shouldShowOverlay) {
      gridRef.current.api.showNoRowsOverlay();
    } else {
      gridRef.current.api.hideOverlay();
    }
    if (shouldShowErrorOverlay) {
      gridRef.current.api.setRowCount(0, true);
    }
  }, [shouldShowOverlay, shouldShowErrorOverlay]);

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    if (!event.api) {
      return;
    }

    const columnStates = event.api.getColumnState();
    const sortedCol = columnStates.find((col) => col.sort !== null);

    if (!sortedCol?.colId) {
      setOrdering(DEFAULT_ORDERING);
      return;
    }

    const direction =
      sortedCol.sort === "asc" ? "ASC_NULLS_LAST" : "DESC_NULLS_LAST";
    const field = COL_ID_TO_ORDERING_FIELD[sortedCol.colId];
    setOrdering(field ? { [field]: direction } : DEFAULT_ORDERING);
  }, []);

  const columnDefs = useMemo<ColDef<BriefingsTableRow>[]>(
    () => [
      {
        colId: "title",
        field: "title",
        headerName: "TITLE",
        minWidth: 200,
        flex: 1.4,
        cellStyle: showStatus
          ? titleCellStyleWithStatusRail
          : titleCellBaseStyle,
        cellRenderer: BriefingsTableTitleCell,
      },
      {
        colId: "schedule",
        field: "schedule",
        headerName: "SCHEDULE",
        minWidth: 160,
        flex: 1.1,
        sortable: false,
        cellRenderer: textCellRenderer,
      },
      {
        colId: "recipients",
        headerName: "RECIPIENTS",
        minWidth: 140,
        width: 140,
        sortable: false,
        cellRenderer: BriefingsTableRecipientsCountCell,
      },
      {
        colId: "status",
        field: "status",
        headerName: "STATUS",
        hide: !showStatus,
        minWidth: 200,
        width: 200,
        sortable: false,
        cellRenderer: BriefingsTableStatusCell,
      },
      {
        colId: "lastGenerated",
        field: "lastGenerated",
        headerName: "LAST GENERATED",
        initialSort: "desc",
        minWidth: 140,
        flex: 1,
        cellRenderer: textCellRenderer,
      },
      {
        colId: "templateName",
        field: "templateName",
        headerName: "TEMPLATE USED",
        minWidth: 120,
        flex: 0.9,
        sortable: false,
        cellRenderer: textCellRenderer,
      },
    ],
    [showStatus],
  );

  const overlayProviderState = useMemo(
    () => ({
      error,
      isFetching: isLoading,
      totalCount,
      isInitialLoading,
      isFiltered,
      onClearFilters,
      clearError: onClearError,
    }),
    [
      error,
      isLoading,
      totalCount,
      isInitialLoading,
      isFiltered,
      onClearFilters,
      onClearError,
    ],
  );

  return (
    <BriefingsTableOverlayProvider state={overlayProviderState}>
      <section
        className="ag-theme-quartz flex min-h-0 w-full min-w-0 flex-1 flex-col"
        style={{ "--ag-grid-min-height": "400px" } as CSSProperties}
        aria-busy={isLoading}
        aria-live="polite"
        aria-label={isLoading ? "Loading briefings" : undefined}
      >
        <AgGridReact<BriefingsTableRow>
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={{
            resizable: true,
            sortable: true,
            cellStyle: { display: "flex", alignItems: "center" },
          }}
          rowModelType="infinite"
          datasource={datasource}
          cacheBlockSize={CACHE_BLOCK_SIZE}
          maxBlocksInCache={MAX_BLOCKS_IN_CACHE}
          infiniteInitialRowCount={INFINITE_INITIAL_ROW_COUNT}
          getRowId={(params) => params.data.id}
          headerHeight={36}
          rowHeight={48}
          theme={agGridTheme}
          enableCellTextSelection
          suppressDragLeaveHidesColumns
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          loadingOverlayComponent={() => null}
          noRowsOverlayComponent={UnifiedOverlay}
          onSortChanged={handleSortChanged}
        />
      </section>
    </BriefingsTableOverlayProvider>
  );
};

export default BriefingsTable;
